import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import {
  VmoModal,
  VmoModalContent,
  VmoModalHeader,
  VmoFormField,
  VmoForm,
  VmoButton,
  VmoModalActions,
  VmoFormGroup,
  VmoFormSelect,
  VmoTooltip,
  VmoCheckbox,
} from 'vmo-library'

import {createContact} from 'actions'
import {getTags} from 'actions/settings'
import {NEW_CONTACT} from 'actions/types'
import {cleanUpObject} from 'utils/helper'
import {countryList} from 'constants/variables'
import {createContactSchema} from 'validation/customers/contact.schema'
import {Input, TextArea} from 'components/common/Form'

const CreateContact = props => {
  const {isCreateContactOpen, setIsCreateContactOpen} = props
  const dispatch = useDispatch()
  const {
    handleSubmit,
    formState: {errors},
    control,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    resolver: joiResolver(createContactSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      allowPortalAccess: false,
      company: {
        name: '',
        url: '',
        address: {country: ''},
      },
      tags: [],
      notes: [{note: ''}],
    },
  })

  const [labelOptionsState, setLabelOptionsState] = useState([])

  const {tagList: labelOptions} = useSelector(state => state.settings)
  const {type, isLoadingData, successLabels = []} = useSelector(state => state.apiReducer)

  useEffect(() => {
    if (!labelOptions) {
      dispatch(getTags())
    }
  }, [])

  useEffect(() => {
    if (labelOptions) {
      const newData = labelOptions.map(({label, id, color, description}) => ({
        value: {label, description, color},
        key: id,
        text: label,
        // label: {
        //   style: { background: color },
        //   empty: true,
        //   circular: true,
        // },
      }))
      setLabelOptionsState(newData)
    }
  }, [labelOptions])

  useEffect(() => {
    if (successLabels.includes(NEW_CONTACT)) {
      setIsCreateContactOpen(false)
    }
  }, [successLabels])

  // final creattion of contact
  const createAction = data => {
    dispatch(createContact(cleanUpObject(data)))
  }

  return (
    <VmoModal
      closeIcon
      open={isCreateContactOpen}
      onClose={() => {
        setIsCreateContactOpen(false)
      }}
      size="small"
      centered={false}
    >
      <VmoModalHeader toggle={() => setIsCreateContactOpen(!isCreateContactOpen)}>Create Customers</VmoModalHeader>
      <VmoModalContent scrolling>
        <VmoForm className="errorLabel">
          <VmoFormGroup widths="equal">
            <Input
              name="firstName"
              control={control}
              label="First Name"
              placeholder="Type your first name.."
              maxLength={20}
              fluid
              type="text"
              required
              tooltipProps={{
                content: 'Specify first name of the customer',
              }}
            />

            <Input
              name="lastName"
              control={control}
              label="Last Name"
              placeholder="Type your last name.."
              maxLength={20}
              required
              fluid
              type="text"
              tooltipProps={{
                content: 'Specify last name of the customer',
              }}
            />
          </VmoFormGroup>
          <VmoFormGroup widths="equal">
            <Input
              name="email"
              control={control}
              label="Email"
              placeholder="Type your email.."
              type="email"
              required
              fluid
              tooltipProps={{
                content: 'Enter the email of the customer',
              }}
            />
            <Input
              name="phone"
              control={control}
              label="Phone No."
              placeholder="Type your number.."
              maxLength={15}
              type="text"
              tooltipProps={{
                content: 'Provide phone number of the customer',
              }}
            />
          </VmoFormGroup>
          <VmoFormField>
            <div className="d-flex">
              <div>
                <label className="mb-2">Allow Access To Client Portal</label>
                <p className="vmo-lead">Allow access to client portal</p>
              </div>
              <Controller
                name="allowPortalAccess"
                control={control}
                render={({field: {value, onChange}}) => (
                  <VmoCheckbox toggle checked={value} onChange={(e, {checked}) => onChange(checked)} />
                )}
              />
            </div>
          </VmoFormField>
          <VmoFormGroup widths="equal">
            <Input
              name="company.name"
              control={control}
              label="Company Name"
              placeholder="Type your company name.."
              maxLength={20}
              fluid
              type="text"
              tooltipProps={{
                content: 'Enter name of the company',
              }}
            />

            <VmoFormField>
              <div className="info-header">
                <label>Company Country</label>
                <VmoTooltip trigger={<AiOutlineInfoCircle />} content="Choose company country from the list" />
              </div>
              <Controller
                name="company.address.country"
                render={({field: {value, onChange, onBlur}}) => (
                  <VmoFormSelect
                    search
                    fluid
                    scrolling
                    value={value}
                    onChange={(e, {value}) => {
                      onChange(value)
                    }}
                    onBlur={onBlur}
                    placeholder="Type your company country.."
                    options={countryList.map(({value, text}) => {
                      return {
                        value,
                        key: value,
                        text,
                      }
                    })}
                  />
                )}
                control={control}
              />
            </VmoFormField>
          </VmoFormGroup>
          <Input
            name="company.url"
            control={control}
            label="Company Website"
            placeholder="Type your company website.."
            maxLength={20}
            fluid
            type="text"
            fieldProps={{
              className: 'mb-1',
            }}
            tooltipProps={{
              content: 'Provide website of the company',
            }}
          />
          <VmoFormField>
            <div className="info-header">
              <label>Tag</label>
              <VmoTooltip trigger={<AiOutlineInfoCircle />} content="Choose tags for the customer" />
            </div>
            <Controller
              name="tags"
              render={({field: {value, onChange}}) => (
                <VmoFormSelect
                  multiple
                  placeholder="Select tag.."
                  value={value}
                  onChange={(e, {value}) => onChange(value)}
                  options={labelOptionsState}
                />
              )}
              control={control}
            />
          </VmoFormField>
          <TextArea
            name="notes[0][note]"
            control={control}
            label="Add Note"
            tooltipProps={{
              content: 'Add small note for the customer',
            }}
            maxLength={500}
            fluid
            placeholder="Add notes here..."
            style={{resize: 'none'}}
          />
        </VmoForm>
      </VmoModalContent>
      <VmoModalActions>
        <VmoButton
          disabled={isLoadingData && type.includes(NEW_CONTACT)}
          basic
          onClick={() => setIsCreateContactOpen(false)}
        >
          Cancel
        </VmoButton>
        <VmoButton
          primary
          loading={isLoadingData && type.includes(NEW_CONTACT)}
          disabled={isLoadingData && type.includes(NEW_CONTACT)}
          onClick={handleSubmit(createAction)}
        >
          Create
        </VmoButton>
      </VmoModalActions>
    </VmoModal>
  )
}

export default CreateContact
